// ViewAsBanner — the persistent "Viewing as {name} · Exit view" bar shown at
// the top of any portal an admin has entered via "View as". It also carries
// the read ⇄ edit toggle (the pencil): read-only by default, and one explicit
// tap flips the server session into edit-on-behalf mode, after which the bar
// reads "Editing as {name}" and exposes the single audited on-behalf write
// (the target's display name).
//
// `ViewAsBannerView` is presentational (used by /brand); `ViewAsBanner` wires
// it to lib/impersonate.

import { useEffect, useState } from "react";
import { Platform, Pressable, Text, TextInput, View, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";

import { FONTS, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";
import {
  exitImpersonation,
  getImpersonatedTargetMeta,
  getImpersonationMode,
  setImpersonationMode,
  updateImpersonatedProfileName,
  type ImpersonationMode,
} from "../../lib/impersonate";

function PencilGlyph({ color }: { color: string }) {
  return (
    <Svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <Path d="M9.5 2.5l2 2L5 11l-2.5.5L3 9l6.5-6.5z" stroke={color} strokeWidth={1.2} strokeLinejoin="round" />
    </Svg>
  );
}

function LockGlyph({ color }: { color: string }) {
  return (
    <Svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <Path d="M4 6.5V4.8a3 3 0 0 1 6 0V6.5M3 6.5h8V12H3z" stroke={color} strokeWidth={1.2} strokeLinejoin="round" />
    </Svg>
  );
}

export function ViewAsBannerView({
  theme,
  name,
  sub,
  mode,
  busy,
  error,
  onToggleMode,
  onExit,
  // inline name editor (edit mode only)
  editingName,
  nameDraft,
  onNameDraftChange,
  onStartEditName,
  onSaveName,
}: {
  theme: PaneTheme;
  name: string;
  sub?: string | null;
  mode: ImpersonationMode;
  busy?: boolean;
  error?: string | null;
  onToggleMode: () => void;
  onExit: () => void;
  editingName: boolean;
  nameDraft: string;
  onNameDraftChange: (v: string) => void;
  onStartEditName: () => void;
  onSaveName: () => void;
}) {
  const isWeb = Platform.OS === "web";
  const editing = mode === "edit";
  // Read mode rides on the pane accent; edit mode shifts to the destructive
  // tone so "writes are live" is unmistakable. Tokens only.
  const bg = editing ? theme.destructive : theme.accent;
  const onBg = theme.pageBg;
  const chipBg = theme.pageBg;

  return (
    <View style={{ backgroundColor: bg }}>
      <View
        style={{
          paddingHorizontal: 16,
          paddingVertical: 8,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Text
          style={{ flex: 1, fontFamily: FONTS.bodyBold, fontSize: 12, letterSpacing: 0.3, color: onBg }}
          numberOfLines={1}
        >
          {editing ? "Editing as " : "Viewing as "}
          {name}
          {sub ? ` · ${sub}` : ""}
        </Text>

        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: STROKES.strong,
            backgroundColor: chipBg,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: bg,
            }}
          >
            {editing ? "Editing — writes live" : "Read-only"}
          </Text>
        </View>

        <BannerButton
          theme={theme}
          onBg={onBg}
          bg={bg}
          disabled={busy}
          icon={editing ? <LockGlyph color={onBg} /> : <PencilGlyph color={onBg} />}
          label={editing ? "Lock" : "Edit"}
          variant="ghost"
          onPress={onToggleMode}
        />
        <BannerButton
          theme={theme}
          onBg={onBg}
          bg={bg}
          icon={null}
          label="Exit view"
          variant="solid"
          onPress={onExit}
        />
      </View>

      {/* Edit-on-behalf: the one audited write. Only when in edit mode. */}
      {editing ? (
        <View
          style={{
            paddingHorizontal: 16,
            paddingBottom: 8,
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: onBg }}>Display name:</Text>
          {editingName ? (
            <>
              <TextInput
                value={nameDraft}
                onChangeText={onNameDraftChange}
                placeholder="Full name"
                placeholderTextColor={onBg}
                style={[
                  {
                    minWidth: 180,
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: STROKES.strong,
                    backgroundColor: theme.pageBg,
                    color: theme.textOnSurface,
                    fontFamily: FONTS.body,
                    fontSize: 12,
                  } as ViewStyle,
                  isWeb ? ({ outlineStyle: "none" } as unknown as object) : null,
                ]}
              />
              <BannerButton
                theme={theme}
                onBg={onBg}
                bg={bg}
                icon={null}
                label={busy ? "Saving…" : "Save"}
                variant="solid"
                disabled={busy}
                onPress={onSaveName}
              />
            </>
          ) : (
            <BannerButton
              theme={theme}
              onBg={onBg}
              bg={bg}
              icon={<PencilGlyph color={onBg} />}
              label="Edit name"
              variant="ghost"
              onPress={onStartEditName}
            />
          )}
          {error ? (
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: onBg }}>{error}</Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function BannerButton({
  onBg,
  bg,
  icon,
  label,
  variant,
  onPress,
  disabled,
}: {
  theme: PaneTheme;
  onBg: string;
  bg: string;
  icon: React.ReactNode;
  label: string;
  variant: "solid" | "ghost";
  onPress: () => void;
  disabled?: boolean;
}) {
  const solid = variant === "solid";
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 5,
          paddingHorizontal: 10,
          paddingVertical: 5,
          borderRadius: STROKES.strong,
          backgroundColor: solid ? onBg : "transparent",
          borderWidth: STROKES.hairline,
          borderColor: onBg,
          opacity: disabled ? 0.6 : 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle)
          : null,
      ]}
    >
      {icon}
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: solid ? bg : onBg,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/** Connected banner — wires the presentational view to the impersonation lib. */
export function ViewAsBanner({ theme }: { theme: PaneTheme }) {
  const [name, setName] = useState<string>("user");
  const [sub, setSub] = useState<string | null>(null);
  const [mode, setMode] = useState<ImpersonationMode>(getImpersonationMode());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  useEffect(() => {
    let cancelled = false;
    void getImpersonatedTargetMeta().then((m) => {
      if (cancelled || !m) return;
      setName(m.fullName ?? m.email ?? m.userId.slice(0, 8));
      setSub(m.companyName ?? m.email ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onToggleMode = async () => {
    setBusy(true);
    setError(null);
    const next: ImpersonationMode = mode === "read" ? "edit" : "read";
    const res = await setImpersonationMode(next);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setMode(next);
    if (next === "read") setEditingName(false);
  };

  const onSaveName = async () => {
    setBusy(true);
    setError(null);
    const res = await updateImpersonatedProfileName(nameDraft);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setName(nameDraft.trim());
    setEditingName(false);
  };

  return (
    <ViewAsBannerView
      theme={theme}
      name={name}
      sub={sub}
      mode={mode}
      busy={busy}
      error={error}
      onToggleMode={() => void onToggleMode()}
      onExit={() => void exitImpersonation()}
      editingName={editingName}
      nameDraft={nameDraft}
      onNameDraftChange={setNameDraft}
      onStartEditName={() => {
        setNameDraft(name);
        setEditingName(true);
      }}
      onSaveName={() => void onSaveName()}
    />
  );
}
